import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/api";
import { Trash2, ArrowRight, ShoppingBag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, shipping, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const goToCheckout = () => {
    if (!user) {
      toast.error("Please sign in to continue to checkout");
      nav("/login?redirect=/checkout");
    } else if (!user.email_verified) {
      nav("/verify-email?send=1");
    } else {
      nav("/checkout");
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <ShoppingBag size={40} className="mx-auto text-[var(--yp-primary)]" />
        <h1 className="font-serif-display text-3xl text-[var(--yp-primary)] mt-4">Your cart is empty</h1>
        <p className="text-[var(--yp-muted)] mt-2">Discover our best-selling indoor plants and start greening your space.</p>
        <Link to="/shop" data-testid="btn-shop-empty" className="inline-flex items-center gap-2 mt-6 btn-primary rounded-full px-6 py-3 text-sm font-medium">Shop plants <ArrowRight size={16} /></Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="mx-auto max-w-6xl px-4 py-10 md:py-14">
      <Seo title="Your Cart" path="/cart" noindex={true} />
      <div className="text-xs uppercase tracking-widest text-[var(--yp-muted)]">Cart</div>
      <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--yp-primary)]">Your basket</h1>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-8">
        <div className="space-y-4">
          {items.map((it) => (
            <div key={`${it.product_id}::${it.variant_id}`} data-testid={`cart-item-${it.product_id}`} className={`flex gap-4 bg-white border border-[var(--yp-border)] rounded-2xl p-4 ${it.unavailable ? "opacity-60" : ""}`}>
              <img src={it.image} alt={it.name} className="w-24 h-24 rounded-xl object-cover bg-[var(--yp-surface-alt)]" />
              <div className="flex-1">
                <Link to={`/product/${it.product_id}`} className="font-serif-display text-lg text-[var(--yp-primary)] hover:underline">{it.name}</Link>
                {it.size && <div className="text-xs text-[var(--yp-muted)]">{it.size}</div>}
                {it.unavailable ? (
                  <div className="text-xs text-[var(--yp-error)] mt-1">No longer available — please remove and re-add a current size</div>
                ) : (
                  <div className="text-sm mt-1">{inr(it.price)}</div>
                )}
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center border border-[var(--yp-border)] rounded-full">
                    <button data-testid={`qty-dec-${it.product_id}`} onClick={() => updateQty(it.product_id, it.variant_id, it.quantity - 1)} className="px-3 py-1.5">−</button>
                    <div className="px-3 min-w-[2ch] text-center">{it.quantity}</div>
                    <button
                      data-testid={`qty-inc-${it.product_id}`}
                      onClick={() => updateQty(it.product_id, it.variant_id, it.quantity + 1)}
                      disabled={typeof it.stock === "number" && it.quantity >= it.stock}
                      className="px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                  <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id, it.variant_id)} className="text-xs text-[var(--yp-error)] hover:underline inline-flex items-center gap-1">
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
                {typeof it.stock === "number" && it.stock > 0 && it.quantity >= it.stock && (
                  <div className="text-xs text-[var(--yp-muted)] mt-1">Only {it.stock} left</div>
                )}
              </div>
              <div className="text-right font-semibold text-[var(--yp-primary)]">{it.unavailable ? "—" : inr(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>

        <aside className="h-fit bg-white border border-[var(--yp-border)] rounded-2xl p-6">
          <div className="font-serif-display text-xl text-[var(--yp-primary)]">Order summary</div>
          <div className="mt-4 text-sm space-y-2">
            <Row label="Subtotal" value={inr(subtotal)} />
            <Row label={`Shipping${shipping === 0 ? " (Free)" : ""}`} value={inr(shipping)} />
            <div className="leaf-divider my-2" />
            <Row label={<span className="font-semibold text-[var(--yp-primary)]">Total</span>} value={<span className="font-semibold text-[var(--yp-primary)] text-lg">{inr(total)}</span>} />
          </div>
          {subtotal < 499 && <div className="text-xs text-[var(--yp-muted)] mt-3">Add {inr(499 - subtotal)} more for free shipping.</div>}
          {user && !user.email_verified && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <AlertTriangle size={14} /> Verify your email to place an order.
            </div>
          )}
          <button data-testid="btn-checkout" onClick={goToCheckout} className="mt-5 block w-full text-center btn-primary rounded-full py-3 text-sm font-medium">
            Proceed to Checkout
          </button>
          <Link to="/shop" className="mt-2 block text-center text-xs text-[var(--yp-muted)] hover:text-[var(--yp-primary)]">Continue shopping</Link>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><div>{label}</div><div>{value}</div></div>;
}
